'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

interface HeaderProps {
  className?: string;
}

const Header = ({ className = '' }: HeaderProps) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navigationLinks = [
    { label: 'Home', href: '/' },
    { label: 'About Nikola', href: '/about-nikola-broceta' },
    { label: 'Career', href: '/career' },
    { label: 'Heritage', href: '/heritage' },
    { label: 'Media', href: '/media-mentions' },
    { label: 'Contact', href: '/contact' },
  ];

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <header className={`sticky top-0 z-50 transition-all duration-300 ${
      isScrolled 
        ? 'bg-bg-1/80 backdrop-blur-xl border-b border-border/50 shadow-institutional' 
        : 'bg-bg-0/50 backdrop-blur-sm border-b border-border/30'
    } ${className}`}>
      <div className="max-w-content mx-auto px-6">
        <div className="flex items-center justify-between h-16">
          <Link 
            href="/" 
            className="flex items-center space-x-3 transition-all duration-200 hover:opacity-80 group"
          >
            <div className="flex flex-col">
              <span className="font-headline text-base font-semibold text-text-primary tracking-tight group-hover:text-accent-green transition-colors duration-200">
                Nikola Broceta
              </span>
              <span className="font-body text-xs text-text-muted">
                Professional Trader & Risk Specialist
              </span>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center space-x-1">
            {navigationLinks.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className="font-body text-sm text-text-primary hover:text-accent-green transition-all duration-200 px-4 py-2 rounded-md relative group"
              >
                {item.label}
                <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-0.5 bg-accent-green group-hover:w-3/4 transition-all duration-300"></span>
              </Link>
            ))}
          </nav>

          <button
            onClick={toggleMobileMenu}
            className="lg:hidden p-2 text-text-primary hover:text-accent-green transition-colors duration-200 rounded-md hover:bg-bg-2"
            aria-label="Toggle mobile menu"
          >
            <Icon 
              name={isMobileMenuOpen ? 'XMarkIcon' : 'Bars3Icon'} 
              size={24} 
              variant="outline"
            />
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="lg:hidden bg-bg-1/95 backdrop-blur-xl border-t border-border/50">
          <nav className="max-w-content mx-auto px-6 py-4 flex flex-col space-y-1">
            {navigationLinks.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                onClick={() => setIsMobileMenuOpen(false)}
                className="font-body text-sm text-text-primary hover:text-accent-green hover:bg-bg-2 transition-all duration-200 py-3 px-4 rounded-md"
              >
                {item.label}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;